import type { DbExecutor } from "../executor";
import { generateId } from "../ids";
import type { ExpenseCategory } from "../../domain/category";
import { isFinancialBucket, type FinancialBucket } from "../../domain/buckets";
import { ValidationError } from "./projectRepository";

interface CategoryRow {
  id: string;
  display_name: string;
  default_bucket: string;
  sort_order: number;
  is_system: number;
  archived_at: string | null;
}

function mapCategory(row: CategoryRow): ExpenseCategory {
  return {
    id: row.id,
    displayName: row.display_name,
    defaultBucket: row.default_bucket as FinancialBucket,
    sortOrder: Number(row.sort_order),
    isSystem: Number(row.is_system) === 1,
    archivedAt: row.archived_at,
  };
}

export class CategoryRepository {
  constructor(private readonly db: DbExecutor) {}

  /** All categories, archived included — history must always resolve. */
  async listAll(): Promise<ExpenseCategory[]> {
    const rows = await this.db.select<CategoryRow>(
      `SELECT id, display_name, default_bucket, sort_order, is_system, archived_at
         FROM expense_categories ORDER BY sort_order, display_name`,
    );
    return rows.map(mapCategory);
  }

  /** Categories offered in new-entry pickers (archived hidden). */
  async listActive(): Promise<ExpenseCategory[]> {
    return (await this.listAll()).filter((c) => c.archivedAt === null);
  }

  async create(displayName: string, defaultBucket: FinancialBucket): Promise<ExpenseCategory> {
    const name = displayName.trim();
    if (name === "") {
      throw new ValidationError(["Category name is required."]);
    }
    if (!isFinancialBucket(defaultBucket)) {
      throw new ValidationError(["A valid default financial bucket is required."]);
    }
    const existing = await this.listAll();
    const maxSort = existing.reduce((m, c) => Math.max(m, c.sortOrder), 0);
    const category: ExpenseCategory = {
      id: generateId(),
      displayName: name,
      defaultBucket,
      sortOrder: maxSort + 10,
      isSystem: false,
      archivedAt: null,
    };
    await this.db.execute(
      `INSERT INTO expense_categories (id, display_name, default_bucket, sort_order, is_system)
       VALUES (?, ?, ?, ?, 0)`,
      [category.id, category.displayName, category.defaultBucket, category.sortOrder],
    );
    return category;
  }

  /** Rename preserves the stable id, so all historical relationships survive. */
  async rename(id: string, displayName: string): Promise<void> {
    const name = displayName.trim();
    if (name === "") {
      throw new ValidationError(["Category name is required."]);
    }
    await this.db.execute("UPDATE expense_categories SET display_name = ? WHERE id = ?", [
      name,
      id,
    ]);
  }

  async setDefaultBucket(id: string, defaultBucket: FinancialBucket): Promise<void> {
    if (!isFinancialBucket(defaultBucket)) {
      throw new ValidationError(["A valid default financial bucket is required."]);
    }
    await this.db.execute("UPDATE expense_categories SET default_bucket = ? WHERE id = ?", [
      defaultBucket,
      id,
    ]);
  }

  /** Archive (soft-delete): hidden from pickers, retained for history. */
  async archive(id: string): Promise<void> {
    await this.db.execute(
      "UPDATE expense_categories SET archived_at = ? WHERE id = ? AND archived_at IS NULL",
      [new Date().toISOString(), id],
    );
  }

  async unarchive(id: string): Promise<void> {
    await this.db.execute("UPDATE expense_categories SET archived_at = NULL WHERE id = ?", [id]);
  }
}
